// A reusable select/dropdown component
import React from "react";

interface Option {
  label: string;
  value: string;
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: Option[];
  required?: boolean;
  searchable?: boolean;
}

type NonSearchableFormSelectProps = Omit<FormSelectProps, "searchable">;

function buildBaseFieldClassName(isCompact: boolean, className: string) {
  return (
    (isCompact
      ? "h-[48px] py-3 px-3 text-base rounded-md border border-[#DCE1E8] focus:border-[#01545D] text-[#1C1C1C] bg-white pr-8 w-full appearance-none"
      : "rounded-md border border-[#DCE1E8] py-3 px-4 w-full pr-10 text-base appearance-none focus:border-[#01545D] text-[#1C1C1C] bg-white") +
    (className ? ` ${className}` : "")
  );
}

const SearchableFormSelect: React.FC<NonSearchableFormSelectProps> = ({
  label,
  options,
  required = false,
  className = "",
  onChange,
  value,
  defaultValue,
  ...props
}) => {
  const isCompact = !label;
  const baseFieldClassName = buildBaseFieldClassName(isCompact, className);

  const selectRef = React.useRef<HTMLSelectElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listboxId = React.useId();

  const [open, setOpen] = React.useState(false);
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const [uncontrolledValue, setUncontrolledValue] = React.useState<string>("");
  const [inputText, setInputText] = React.useState("");

  const isControlled = value != null;
  const selectedValue = isControlled ? String(value) : uncontrolledValue;
  const placeholderOption = options.find((o) => o.value === "");
  const selectedLabel =
    selectedValue === ""
      ? ""
      : options.find((o) => o.value === selectedValue)?.label ??
        options.find((o) => o.value === (options[0]?.value ?? ""))?.label ??
        "";

  const normalizedQuery = inputText.trim().toLowerCase();
  const optionsWithoutPlaceholder = placeholderOption
    ? options.filter((o) => o.value !== "")
    : options;
  const filteredOptions = normalizedQuery
    ? optionsWithoutPlaceholder.filter((o) => o.label.toLowerCase().includes(normalizedQuery))
    : optionsWithoutPlaceholder;

  React.useEffect(() => {
    // Keep input text consistent with the actual selected option.
    // For uncontrolled selects, the browser may fall back to the first option if defaultValue doesn't match.
    const v = selectRef.current?.value ?? "";
    if (!isControlled) {
      setUncontrolledValue(v);
    }
    const labelForV = v === "" ? "" : options.find((o) => o.value === v)?.label ?? "";
    setInputText(labelForV);
  }, [isControlled, options]);

  React.useEffect(() => {
    if (isControlled) {
      setInputText(selectedLabel);
    }
  }, [isControlled, selectedLabel]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!isControlled) {
      setUncontrolledValue(e.target.value);
    }
    const nextLabel = options.find((o) => o.value === e.target.value)?.label ?? "";
    setInputText(e.target.value === "" ? "" : nextLabel);
    onChange?.(e);
  };

  const commitValue = (nextValue: string) => {
    const selectEl = selectRef.current;
    if (!selectEl) return;

    selectEl.value = nextValue;
    selectEl.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const handlePick = (opt: Option) => {
    commitValue(opt.value);
    setOpen(false);
    setHighlightIndex(0);
    inputRef.current?.focus();
  };

  return (
    <div className={isCompact ? "w-full" : "flex flex-col gap-2 w-full"}>
      {label && (
        <label className="font-semibold text-[#1C1C1C] text-base">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          placeholder={placeholderOption?.label}
          onChange={(e) => {
            setInputText(e.target.value);
            setOpen(true);
            setHighlightIndex(0);
          }}
          onFocus={() => {
            setOpen(true);
            setHighlightIndex(0);
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setOpen(false);
              setHighlightIndex(0);
              setInputText(
                (selectRef.current?.value ?? "") === ""
                  ? ""
                  : options.find((o) => o.value === (selectRef.current?.value ?? ""))?.label ?? ""
              );
            }, 100);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setHighlightIndex((i) =>
                filteredOptions.length ? (i + 1) % filteredOptions.length : 0
              );
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setOpen(true);
              setHighlightIndex((i) =>
                filteredOptions.length
                  ? (i - 1 + filteredOptions.length) % filteredOptions.length
                  : 0
              );
            } else if (e.key === "Enter") {
              if (open && filteredOptions[highlightIndex]) {
                e.preventDefault();
                handlePick(filteredOptions[highlightIndex]);
              }
            } else if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              setHighlightIndex(0);
              setInputText(selectedLabel);
            }
          }}
          className={baseFieldClassName.replace("appearance-none", "")}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && filteredOptions[highlightIndex] ? `${listboxId}-opt-${highlightIndex}` : undefined
          }
        />

        <select
          ref={selectRef}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          required={required}
          value={value as any}
          defaultValue={defaultValue as any}
          onChange={handleSelectChange}
          tabIndex={-1}
          aria-hidden="true"
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value || opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {open && filteredOptions.length > 0 && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-10 mt-1 w-full bg-white border border-[#DCE1E8] rounded-md shadow-md max-h-60 overflow-auto"
          >
            {filteredOptions.map((opt, idx) => {
              const isActive = idx === highlightIndex;
              const isSelected = opt.value === (selectRef.current?.value ?? "");
              return (
                <button
                  key={`${opt.value}-${idx}`}
                  id={`${listboxId}-opt-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  type="button"
                  className={
                    "w-full text-left px-3 py-2 text-sm " +
                    (isActive ? "bg-[#F7F8FD]" : "bg-white hover:bg-[#F7F8FD]") +
                    (isSelected ? " font-semibold" : "")
                  }
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => handlePick(opt)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        <svg
          className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

const FormSelect: React.FC<FormSelectProps> = ({ searchable = false, ...rest }) => {
  if (searchable) {
    return <SearchableFormSelect {...rest} />;
  }

  const { label, options, required = false, className = "", onChange, value, defaultValue, ...props } =
    rest;

  const isCompact = !label;
  const baseFieldClassName = buildBaseFieldClassName(isCompact, className);

  return (
    <div className={isCompact ? "w-full" : "flex flex-col gap-2 w-full"}>
      {label && (
        <label className="font-semibold text-[#1C1C1C] text-base">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          className={baseFieldClassName}
          required={required}
          value={value as any}
          defaultValue={defaultValue as any}
          onChange={onChange}
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value || opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <svg
          className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default FormSelect;
